/**
 * Respuestas DIRECTAS de las consultas rápidas: pegan a los endpoints de métricas
 * (BridgeHub) y formatean el texto localmente — SIN pasar por Claude. Así los
 * botones precargados NO consumen tokens/costo de API. Solo el texto libre que
 * el usuario escribe usa el modelo.
 */
import type {
  CustomQuery,
  DashboardView,
  KpiData,
  PaymentSlice,
  PeriodSpec,
  ProductItem,
  RankItem,
  ScopeFilters,
  TrendPoint,
} from '@contracts';
import type { MetricKey } from '@contracts';
import type { WidgetSuggestion } from '@/services/assistantService';
import { fetchMetric } from '@/services/metricService';
import { fetchCustomMetric } from '@/services/customMetricsService';

export interface PresetCtx {
  period: PeriodSpec;
  companies: string[];
  view: DashboardView;
  scope: ScopeFilters;
}
export interface PresetResult {
  answer: string;
  suggestions?: WidgetSuggestion[];
}

const money = (n: number) =>
  Math.round(n).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });
const num = (n: number) => Math.round(n).toLocaleString('es-MX');

async function kpi(ctx: PresetCtx, metric: MetricKey): Promise<KpiData> {
  const r = await fetchMetric({ metric, view: ctx.view, period: ctx.period, companies: ctx.companies, scope: ctx.scope });
  return r.data as KpiData;
}
async function list<T>(ctx: PresetCtx, metric: MetricKey): Promise<T> {
  const r = await fetchMetric({ metric, view: ctx.view, period: ctx.period, companies: ctx.companies, scope: ctx.scope });
  return r.data as T;
}
async function customVal(ctx: PresetCtx, query: CustomQuery): Promise<number> {
  const r = await fetchCustomMetric(query, ctx.period, ctx.companies);
  return (r.data as KpiData)?.value ?? 0;
}
const sug = (metric: MetricKey): WidgetSuggestion[] => [{ kind: 'metric', metric }];

/** id de consulta rápida → resolver (respuesta directa, sin IA). */
export const PRESETS: Record<string, (ctx: PresetCtx) => Promise<PresetResult>> = {
  sales: async (ctx) => {
    const k = await kpi(ctx, 'sales');
    return { answer: `Vendiste **${money(k.value)}** (neto) en el periodo seleccionado.`, suggestions: sug('sales') };
  },
  avgTicket: async (ctx) => {
    const k = await kpi(ctx, 'avgTicket');
    return { answer: `El **ticket promedio** es **${money(k.value)}** por compra.`, suggestions: sug('avgTicket') };
  },
  units: async (ctx) => {
    const k = await kpi(ctx, 'products');
    return { answer: `Vendiste **${num(k.value)}** unidades de producto.`, suggestions: sug('products') };
  },
  newClients: async (ctx) => {
    const k = await kpi(ctx, 'newClients');
    return { answer: `Registraste **${num(k.value)}** clientes nuevos en el periodo.`, suggestions: sug('newClients') };
  },
  compareMonths: async (ctx) => {
    const base = { view: ctx.view, companies: ctx.companies, scope: ctx.scope } as const;
    const thisM = (await fetchMetric({ metric: 'sales', period: 'this_month', ...base })).data as KpiData;
    const lastM = (await fetchMetric({ metric: 'sales', period: 'last_month', ...base })).data as KpiData;
    const diff = lastM.value ? ((thisM.value - lastM.value) / lastM.value) * 100 : 0;
    const arrow = diff >= 0 ? '▲' : '▼';
    return {
      answer: `Este mes: **${money(thisM.value)}** · Mes pasado: **${money(lastM.value)}**\n${arrow} ${Math.abs(diff).toFixed(1)}% vs el mes anterior.`,
      suggestions: sug('sales'),
    };
  },
  bestDay: async (ctx) => {
    const pts = await list<TrendPoint[]>(ctx, 'dailyTrend');
    if (!pts.length) return { answer: 'No hay ventas en el periodo seleccionado.' };
    const best = pts.reduce((a, b) => (b.total > a.total ? b : a));
    return { answer: `Tu **mejor día** fue **${best.date}** con **${money(best.total)}** en ventas.`, suggestions: sug('dailyTrend') };
  },
  trend: async (ctx) => {
    const pts = await list<TrendPoint[]>(ctx, 'dailyTrend');
    if (!pts.length) return { answer: 'No hay ventas en el periodo seleccionado.' };
    const total = pts.reduce((s, p) => s + p.total, 0);
    const best = pts.reduce((a, b) => (b.total > a.total ? b : a));
    return {
      answer: `En el periodo: total **${money(total)}** en ${pts.length} días. Mejor día: **${best.date}** (${money(best.total)}).`,
      suggestions: sug('dailyTrend'),
    };
  },
  bestSeller: async (ctx) => {
    const r = await list<RankItem[]>(ctx, 'sellersRanking');
    if (!r.length) return { answer: 'No hay datos de vendedores en el periodo.' };
    const top = r[0];
    return { answer: `El **mejor vendedor** es **${top.name}** con **${money(top.value)}** (${top.orders} órdenes).`, suggestions: sug('sellersRanking') };
  },
  branches: async (ctx) => {
    const r = await list<RankItem[]>(ctx, 'branchesRanking');
    if (!r.length) return { answer: 'No hay datos de sucursales en el periodo.' };
    const lines = r.slice(0, 5).map((it, i) => `${i + 1}. **${it.name}** — ${money(it.value)}`).join('\n');
    return { answer: `**Ranking de sucursales:**\n${lines}`, suggestions: sug('branchesRanking') };
  },
  productsTop: async (ctx) => {
    const r = await list<ProductItem[]>(ctx, 'productsTop');
    if (!r.length) return { answer: 'No hay productos vendidos en el periodo.' };
    const lines = r.slice(0, 5).map((p, i) => `${i + 1}. **${p.name}** — ${num(p.qty)} u. (${money(p.total)})`).join('\n');
    return { answer: `**Productos más vendidos:**\n${lines}`, suggestions: sug('productsTop') };
  },
  productsBottom: async (ctx) => {
    const r = await list<ProductItem[]>(ctx, 'productsBottom');
    if (!r.length) return { answer: 'No hay productos en el periodo.' };
    const lines = r.slice(0, 5).map((p, i) => `${i + 1}. **${p.name}** — ${num(p.qty)} u.`).join('\n');
    return { answer: `**Productos menos vendidos:**\n${lines}`, suggestions: sug('productsBottom') };
  },
  paymentMix: async (ctx) => {
    const r = await list<PaymentSlice[]>(ctx, 'paymentMix');
    if (!r.length) return { answer: 'No hay pagos en el periodo.' };
    const lines = r.slice(0, 6).map((s, i) => `${i + 1}. **${s.method}** — ${money(s.amount)}`).join('\n');
    return { answer: `**Mix de métodos de pago:**\n${lines}`, suggestions: sug('paymentMix') };
  },
  apartadosActivos: async (ctx) => {
    const v = await customVal(ctx, {
      model: 'pos.wishlist',
      aggregation: 'count',
      domain: [{ field: 'state', op: '=', value: 'active' }],
      visualization: 'kpi',
    });
    return { answer: `Hay **${num(v)}** apartados **activos** actualmente.` };
  },
  porCobrar: async (ctx) => {
    const v = await customVal(ctx, {
      model: 'pos.wishlist',
      aggregation: 'sum',
      field: 'amount_pending',
      domain: [{ field: 'state', op: '=', value: 'active' }],
      visualization: 'kpi',
    });
    return { answer: `Monto **por cobrar** en apartados activos: **${money(v)}**.` };
  },
  albaranes: async (ctx) => {
    const v = await customVal(ctx, {
      model: 'stock.picking',
      aggregation: 'count',
      domain: [{ field: 'state', op: 'in', value: ['assigned', 'confirmed', 'waiting'] }],
      visualization: 'kpi',
    });
    return { answer: `Hay **${num(v)}** albaranes/transferencias **pendientes**.` };
  },
  storeCredit: async (ctx) => {
    const v = await customVal(ctx, {
      model: 'partner.store.credit',
      aggregation: 'sum',
      field: 'balance_available',
      domain: [{ field: 'state', op: '=', value: 'active' }],
      visualization: 'kpi',
    });
    return { answer: `**Store credit** disponible (activo): **${money(v)}**.` };
  },

  // ─── LOGÍSTICA / INVENTARIO (consultas rápidas del asistente 'dashlog') ───
  urgencyKpis: async (ctx) => {
    const k = await kpi(ctx, 'urgencyKpis') as import('@contracts').UrgencyKpiData;
    return {
      answer: `🚨 **${num(k.pedirYa ?? 0)}** productos **a pedir YA** · ${num(k.pedirSemana ?? 0)} esta semana · ${num(k.evaluated ?? 0)} evaluados.`,
      suggestions: sug('urgencyKpis'),
    };
  },
  resupplyUrgent: async (ctx) => {
    const rows = await list<import('@contracts').ForecastRow[]>(ctx, 'resupplyUrgent');
    if (!rows.length) return { answer: '✅ No hay productos urgentes por pedir.' };
    const top = rows.slice(0, 5).map((r) => `• ${r.name} (${r.pos ?? '—'}): pedir **${num(r.units ?? 0)}**`).join('\n');
    return { answer: `**${num(rows.length)}** productos a pedir YA. Los más urgentes:\n${top}`, suggestions: sug('resupplyUrgent') };
  },
  resupplyForecast: async (ctx) => {
    const rows = await list<import('@contracts').ForecastRow[]>(ctx, 'resupplyForecast');
    if (!rows.length) return { answer: 'No hay predicción de resurtido para el scope actual.' };
    const top = rows.slice(0, 5).map((r) => `• ${r.name}: se quiebra ${r.breakDate ?? '—'}, pedir ${num(r.units ?? 0)}`).join('\n');
    return { answer: `**Calendario de resurtido** (${num(rows.length)} productos). Próximos:\n${top}`, suggestions: sug('resupplyForecast') };
  },
  inventoryValue: async (ctx) => {
    const k = await kpi(ctx, 'inventoryValuation') as import('@contracts').InventoryValuationData;
    return {
      answer: `💰 **Valor de inventario:** ${money(k.value)} · ${num(k.units ?? 0)} unidades en ${num(k.locations ?? 0)} ubicaciones.`,
      suggestions: sug('inventoryValuation'),
    };
  },
  inventoryByLocation: async (ctx) => {
    const rows = await list<import('@contracts').ValuationRow[]>(ctx, 'inventoryValuationByLocation');
    if (!rows.length) return { answer: 'Sin inventario en el scope actual.' };
    const top = rows.slice(0, 5).map((r) => `• ${r.name}: ${money(r.value)}`).join('\n');
    return { answer: `**Inventario por ubicación** (top):\n${top}`, suggestions: sug('inventoryValuationByLocation') };
  },
  resupplyCount: async (ctx) => {
    const k = await kpi(ctx, 'resupplyCount');
    return { answer: `📦 **${num(k.value)}** productos a resurtir en total.`, suggestions: sug('resupplyCount') };
  },
  bringFromStores: async (ctx) => {
    const rows = await list<import('@contracts').LogisticsRow[]>(ctx, 'redistributionList');
    if (!rows.length) return { answer: 'No hay productos sugeridos para traer de otras tiendas.' };
    const top = rows.slice(0, 5).map((r) => `• ${r.name}: traer **${num(r.transferQty ?? r.units ?? 0)}** desde ${r.fromPos ?? '—'}`).join('\n');
    return { answer: `**Traer de otras tiendas** (${num(rows.length)} sugerencias):\n${top}`, suggestions: sug('redistributionList') };
  },
  stockTransfers: async (ctx) => {
    const rows = await list<import('@contracts').MovementRow[]>(ctx, 'stockTransfers');
    if (!rows.length) return { answer: 'No hubo transferencias entre tiendas en el periodo.' };
    const top = rows.slice(0, 5).map((r) => `• ${r.name}: ${r.from} → ${r.to} (${num(r.units)})`).join('\n');
    return { answer: `**Transferencias entre tiendas** (${num(rows.length)}):\n${top}`, suggestions: sug('stockTransfers') };
  },
  purchaseOrders: async (ctx) => {
    const rows = await list<import('@contracts').PurchaseRow[]>(ctx, 'purchasesList');
    if (!rows.length) return { answer: 'No hay órdenes de compra en el periodo.' };
    const top = rows.slice(0, 5).map((r) => `• ${r.order} — ${r.supplier} (${r.state}): ${money(r.amount)}`).join('\n');
    return { answer: `**Órdenes de compra** (${num(rows.length)}):\n${top}`, suggestions: sug('purchasesList') };
  },
};
